import { useQuery } from '@apollo/client/react';
import { ZonedDateTime } from '@js-joda/core';
import {
  Box,
  Button,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  Datagrid,
  FunctionField,
  ListContextProvider,
  Pagination,
  ResourceContextProvider,
  TextField,
  Title,
  useList,
} from 'react-admin';
import { Link } from 'react-router-dom';
import {
  AdminClientLaunchesDocument,
  AdminClientLaunchesQuery,
  ClientLaunchStatus,
  ClientLaunchType,
} from '../../../graphql/types';
import useCanWrite from '../../../hooks/useCanWrite';
import { getDurationSince } from '../../../utils/date';
import { CustomTabPanel } from '../components/CustomTabPanel';
import { getLaunchProgress } from './launchState';
import { useLaunchPaths } from './paths';
import { getLaunchSteps } from './steps';

type ClientLaunchNode =
  AdminClientLaunchesQuery['clientLaunches']['nodes'][number];

const formatLaunchType = ({ type, features }: ClientLaunchNode) => {
  if (type === ClientLaunchType.NewClient) {
    return 'New Client';
  }
  const featureNames = features.map((feature) => feature.name).join(', ');
  return featureNames ? `Feature launch - ${featureNames}` : 'Feature launch';
};

const renderClient = (launch: ClientLaunchNode) => (
  <Box>
    <Typography variant="body2" fontWeight="bold">
      {launch.client.displayName}
    </Typography>
    <Typography variant="body2">{formatLaunchType(launch)}</Typography>
  </Box>
);

const formatOwner = ({ owner }: ClientLaunchNode) =>
  owner ? `${owner.firstName} ${owner.lastName}` : '';

const renderProgress = (launch: ClientLaunchNode) => {
  const { stepNumber, stepCount, stepTitle, completeCount } = getLaunchProgress(
    getLaunchSteps(launch.type),
    launch,
  );
  return (
    <Box minWidth={160}>
      <Typography variant="body2">
        {`Step ${stepNumber} of ${stepCount} - ${stepTitle}`}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={(completeCount / stepCount) * 100}
      />
    </Box>
  );
};

interface ClientLaunchesResult {
  launches: ClientLaunchNode[];
  loading: boolean;
  error?: any;
}

const useClientLaunches = (
  status: ClientLaunchStatus,
): ClientLaunchesResult => {
  const { data, loading, error } = useQuery(AdminClientLaunchesDocument, {
    variables: { filter: { status } },
    // Launches started or moved on in the flow aren't in the cached list.
    fetchPolicy: 'cache-and-network',
  });

  return { launches: data?.clientLaunches.nodes ?? [], loading, error };
};

const formatTabLabel = (
  label: string,
  { launches, loading }: ClientLaunchesResult,
) => (loading ? label : `${label} (${launches.length})`);

interface LaunchManagerListProps {
  status: ClientLaunchStatus;
  launches: ClientLaunchNode[];
  loading: boolean;
  error?: any;
}

const EmptyLaunchManager: React.FC<{ status: ClientLaunchStatus }> = (
  status,
) => {
  return (
    <Box>
      <Typography>{`${status.status} client/feature launches will appear here.`}</Typography>
    </Box>
  );
};

const LaunchManagerList: React.FC<LaunchManagerListProps> = ({
  status,
  launches,
  loading,
  error,
}) => {
  const paths = useLaunchPaths();
  const canWrite = useCanWrite();
  const listContext = useList({
    data: launches,
    isPending: loading,
    error: error,
    perPage: 25,
  });

  if (error) {
    return (
      <Typography variant="subtitle1" color="error">
        Error fetching Launches
      </Typography>
    );
  }

  return (
    <ResourceContextProvider value="clientLaunches">
      <ListContextProvider value={listContext}>
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          sx={{
            bgcolor: 'background.paper',
          }}
          empty={<EmptyLaunchManager status={status} />}
        >
          <FunctionField label="Client" render={renderClient} />
          {status === ClientLaunchStatus.InProgress && (
            <FunctionField label="Progress" render={renderProgress} />
          )}
          {status === ClientLaunchStatus.InProgress && (
            <TextField source="waitingOn" label="Waiting On" />
          )}
          <FunctionField label="Owner" render={formatOwner} />
          <FunctionField
            label="Updated"
            render={({ updatedAt }: ClientLaunchNode) =>
              getDurationSince(ZonedDateTime.parse(updatedAt))
            }
          />
          <FunctionField
            render={({ id }: ClientLaunchNode) => (
              <Button
                variant="outlined"
                size="small"
                component={Link}
                to={paths.launch(id)}
              >
                {canWrite && status === ClientLaunchStatus.InProgress
                  ? 'Resume'
                  : 'View'}
              </Button>
            )}
          />
        </Datagrid>
        <Pagination />
      </ListContextProvider>
    </ResourceContextProvider>
  );
};

const LaunchManagerTabs: React.FC = () => {
  const [tabValue, setTabValue] = React.useState(0);

  const handleChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const inProgress = useClientLaunches(ClientLaunchStatus.InProgress);
  const finished = useClientLaunches(ClientLaunchStatus.Finished);
  const abandoned = useClientLaunches(ClientLaunchStatus.Abandoned);

  return (
    <>
      <Tabs value={tabValue} onChange={handleChange}>
        <Tab
          label={formatTabLabel('In Progress', inProgress)}
          id="launch-in-progress-tab"
          aria-label="Launches in progress"
        />
        <Tab
          label={formatTabLabel('Finished', finished)}
          id="launch-finished-tab"
          aria-label="Finished launches"
        />
        <Tab
          label={formatTabLabel('Abandoned', abandoned)}
          id="launch-abandoned-tab"
          aria-label="Abandoned launches"
        />
      </Tabs>
      <CustomTabPanel index={0} value={tabValue}>
        <LaunchManagerList
          status={ClientLaunchStatus.InProgress}
          {...inProgress}
        />
      </CustomTabPanel>
      <CustomTabPanel index={1} value={tabValue}>
        <LaunchManagerList status={ClientLaunchStatus.Finished} {...finished} />
      </CustomTabPanel>
      <CustomTabPanel index={2} value={tabValue}>
        <LaunchManagerList
          status={ClientLaunchStatus.Abandoned}
          {...abandoned}
        />
      </CustomTabPanel>
    </>
  );
};

const LaunchManager: React.FC = () => {
  const paths = useLaunchPaths();
  const canWrite = useCanWrite();
  return (
    <Box marginTop={2}>
      <Title title="Launch Manager" />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" flexDirection="column">
          <Typography variant="h3">Launch Manager</Typography>
          <Typography variant="subtitle1">
            Launch a new client or feature. Progress is saved at every step and
            anyone can pick up a launch in progress.
          </Typography>
        </Box>
        {canWrite && (
          <Button variant="contained" component={Link} to={paths.newClient}>
            Start a setup
          </Button>
        )}
      </Box>
      <LaunchManagerTabs />
    </Box>
  );
};

export default LaunchManager;
